import { CloseOutlineIcon } from '@applyboard/ui-icons';
import * as RadixDialog from '@radix-ui/react-dialog';
import React from 'react';
import { Button } from '../Button';
import { Heading } from '../Heading';
import { useFullTheme } from '~/design';
import { findChildOfType, isChildOneOfType, useId, useResponsive } from '~/utils';

interface DrawerContextContextProps {
  onOpenChange: (isOpen: boolean) => void;
  titleId: string;
}

const DrawerContext = React.createContext<DrawerContextContextProps | null>(null);

export interface DrawerProps {
  /** The aria-label of the drawer. If the drawer has a title, this prop cannot be supplied. */
  'aria-label'?: string;

  /** The aria-labelledby of the drawer. Useful if you want to use an element outside of the drawer
   * to describe the drawer. */
  'aria-labelledby'?: string;

  /** The content of the drawer. */
  children: React.ReactChild | React.ReactFragment | React.ReactPortal;

  /** A callback function that will get called when the drawer is opened or closed. */
  onOpenChange: (isOpen: boolean) => void;

  /** Whether or not the drawer is open. */
  open?: boolean;

  /** Determines which side of the window the drawer slides out from. "top" and "bottom" placements
   * should only be used for mobile devices. */
  placement: 'left' | 'right' | 'top' | 'bottom';

  /** The width of the drawer. */
  width?: 'sm' | 'md';

  /** The id of the heading element. */
  headingId?: string;
}

/**
 * Drawer is a collapsible panel that can overlay the contents of the screen.
 */
export function Drawer({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  children,
  onOpenChange,
  open = false,
  placement,
  width,
  headingId,
}: DrawerProps) {
  const theme = useFullTheme();
  const responsive = useResponsive();
  const randomTitleId = useId();
  const titleId = headingId ?? randomTitleId;

  const headingChild = findChildOfType(children, Drawer.Header);
  const footerChild = findChildOfType(children, Drawer.Footer);
  const triggerChild = findChildOfType(children, Drawer.Trigger);
  const otherChildren = React.Children.toArray(children).filter(
    child => !isChildOneOfType(child, [Drawer.Footer, Drawer.Header, Drawer.Trigger]),
  );

  const isVertical = ['top', 'bottom'].includes(placement);

  if (!!ariaLabel && !!headingChild) {
    throw new Error('Cannot use "aria-label" in conjunction with Drawer.Header.');
  }
  if (!!ariaLabel && !!ariaLabelledBy) {
    throw new Error('Only one of "aria-label" and "aria-labelledby" props may be supplied.');
  }
  if (!ariaLabel && !ariaLabelledBy && !headingChild) {
    throw new Error(
      'Drawer is inaccessible. Please supply one of: "aria-label", "aria-labelledby", or use Drawer.Header.',
    );
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      {triggerChild}

      <RadixDialog.Portal>
        <RadixDialog.Overlay
          css={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            inset: 0,
            position: 'fixed',
            zIndex: theme.zIndex.drawer.overlay,
          }}
        />

        <RadixDialog.Content
          // Radix sets an aria-describedby to an element that does not exist, so override it
          aria-describedby={undefined}
          aria-label={headingChild ? undefined : ariaLabel}
          aria-labelledby={headingChild ? titleId : ariaLabelledBy}
          asChild
          css={[
            {
              [placement]: 0,
              backgroundColor: theme.colors.bgRegular,
              display: 'flex',
              flexDirection: 'column',
              minHeight: isVertical ? 'auto' : '100vh',
              maxHeight: '100vh',
              position: 'fixed',
              zIndex: open ? theme.zIndex.drawer.content : undefined,
            },
            responsive.css({
              maxWidth: {
                xs: '100%',
                sm: isVertical ? '100%' : width === 'sm' ? '400px' : '600px',
                md: width === 'sm' ? '400px' : '600px',
                lg: width === 'sm' ? '400px' : '600px',
                xl: width === 'sm' ? '400px' : '600px',
              },
              width: {
                xs: '100%',
                sm: isVertical ? '100%' : width === 'sm' ? '400px' : '600px',
                md: width === 'sm' ? '400px' : '600px',
                lg: width === 'sm' ? '400px' : '600px',
                xl: width === 'sm' ? '400px' : '600px',
              },
            }),
            !isVertical && {
              top: 0,
            },
            isVertical && {
              left: 0,
            },
          ]}
        >
          <aside>
            <DrawerContext.Provider value={{ titleId, onOpenChange }}>
              {headingChild}
              <DrawerBody>{otherChildren}</DrawerBody>
              {footerChild}
            </DrawerContext.Provider>
          </aside>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export interface DrawerTriggerProps {
  /** The children of the trigger. */
  children: React.ReactElement;
}

Drawer.Trigger = function DrawerTrigger(props: DrawerTriggerProps) {
  return (
    <RadixDialog.Trigger asChild>
      <TriggerAdapter>{props.children}</TriggerAdapter>
    </RadixDialog.Trigger>
  );
};

const TriggerAdapter = React.forwardRef<
  HTMLButtonElement,
  { children: React.ReactElement; onClick?: (event: unknown) => void }
>(function TriggerAdapter({ children, onClick }, ref) {
  const child = React.Children.only(children);
  return (
    <child.type
      {...child.props}
      ref={ref}
      onClick={() => {
        onClick?.({ target: {} });
      }}
    />
  );
});

export interface DrawerHeaderProps {
  /** The text of the header. */
  children: string;
}

Drawer.Header = function DrawerHeader({ children }: DrawerHeaderProps) {
  const context = React.useContext(DrawerContext);

  if (!context) {
    throw new Error('`Drawer.Header` can only be rendered inside `Drawer`');
  }
  if (typeof children !== 'string') {
    throw new Error('A string must be used as the child of Drawer.Header');
  }

  const { titleId, onOpenChange } = context;
  const theme = useFullTheme();

  return (
    <header
      css={{
        alignItems: 'start',
        display: 'flex',
        justifyContent: 'space-between',
        padding: theme.spacing[4],
      }}
    >
      <Heading id={titleId} level={2}>
        {children}
      </Heading>
      <Button
        aria-label="Close"
        emphasis="transparent"
        intent="secondary"
        leadIcon={CloseOutlineIcon}
        onClick={() => onOpenChange(false)}
        size="sm"
      />
    </header>
  );
};

export interface DrawerBodyProps {
  children: React.ReactChild | React.ReactFragment | React.ReactPortal;
}

function DrawerBody({ children }: DrawerBodyProps) {
  const theme = useFullTheme();

  return (
    <div
      css={{
        flex: '1 1 auto',
        minHeight: '0px',
        overflowY: 'auto',
        padding: theme.spacing[4],
        background: `linear-gradient(
        white 30%,
        rgba(255, 255, 255, 0)
      ) center top,
      linear-gradient(
        rgba(255, 255, 255, 0),
        white 70%
      ) center bottom,
      radial-gradient(
        farthest-side at 50% 0,
        rgba(0, 0, 0, 0.2),
        rgba(0, 0, 0, 0)
      ) center top,
      radial-gradient(
        farthest-side at 50% 100%,
        rgba(0, 0, 0, 0.2),
        rgba(0, 0, 0, 0)
      ) center bottom`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 40px, 100% 40px, 100% 14px, 100% 14px',
        backgroundAttachment: 'local, local, scroll, scroll',
      }}
    >
      {children}
    </div>
  );
}

export interface DrawerFooterProps {
  /** The children of the footer. */
  children: React.ReactNode;
}

Drawer.Footer = function DrawerFooter({ children }: DrawerFooterProps) {
  if (!React.useContext(DrawerContext)) {
    throw new Error('`Drawer.Footer` can only be rendered inside `Drawer`');
  }

  const theme = useFullTheme();

  return (
    <footer
      css={{
        borderTopColor: theme.borders.regular.borderColor,
        borderTopStyle: theme.borders.regular.borderStyle,
        borderTopWidth: theme.borders.regular.borderWidth,
        padding: theme.spacing[2],
      }}
    >
      {children}
    </footer>
  );
};
